// Content model for the public marketing site.
// The platform started as an auditorium booking tool; it's now a full campus
// operations platform — events, halls, graduation day, BICEP incubation,
// ProjectHub — all gated per-institute via the multi-tenant KV registry.
import {
  CalendarCheck, ShieldCheck, Megaphone, QrCode, Award, Users,
  BookOpen, ClipboardList, MapPin, GraduationCap, Rocket, FolderKanban,
  Building2, Layers, ScanLine, FileCheck2,
} from "lucide-react";

export const NAV_LINKS = [
  { to: "/features",     label: "Features" },
  { to: "/how-it-works", label: "Modules" },
  { to: "/events",       label: "Public events" },
  { to: "/contact",      label: "Contact" },
];

// ── Hero audience cards ────────────────────────────────────────────────────
export const AUDIENCES = [
  {
    key: "student",
    icon: BookOpen,
    title: "Students & clubs",
    tagline: "Register, apply, submit — all from one login.",
    accent: "from-cobalt-500 to-cobalt-700",
    perks: ["Event tickets", "BICEP apply", "Project submit"],
  },
  {
    key: "faculty",
    icon: Users,
    title: "Faculty & organisers",
    tagline: "Book halls, run events, track projects.",
    accent: "from-cyanx to-cobalt-500",
    perks: ["Hall requests", "Event pages", "Project mentor"],
  },
  {
    key: "admin",
    icon: ShieldCheck,
    title: "Admins & incharges",
    tagline: "Oversight across every module on your campus.",
    accent: "from-chartreuse to-cyanx",
    perks: ["Approvals", "Grad day", "Incubatees"],
  },
];

// ── Features grid (shown on /features and as a teaser on home) ────────────
export const FEATURES = [
  {
    icon: CalendarCheck,
    title: "Hall booking & approvals",
    desc: "Request any venue by date and slot. Overlaps are caught before submission; the hall incharge approves or declines with a single action and everyone gets notified.",
  },
  {
    icon: Megaphone,
    title: "Event pages & registration",
    desc: "Turn a booking into a public event page in one click. Seat caps, waitlists and department filters are built in — no third-party form tools needed.",
  },
  {
    icon: QrCode,
    title: "QR check-in",
    desc: "Every ticket carries a unique QR code. Scan at the gate, walk-in registration, and live attendance counts all happen in the same pane.",
  },
  {
    icon: Award,
    title: "Verifiable certificates",
    desc: "Issue participation certificates in bulk after the event. Each has a public verification link — no PDF forgeries, no manual issuing.",
  },
  {
    icon: GraduationCap,
    title: "Graduation Day module",
    desc: "End-to-end graduation management: student confirmation by email OTP, guardian portal, kiosk display, staff QR scanner and a reporting dashboard.",
  },
  {
    icon: Rocket,
    title: "BICEP Incubation (BMSIT)",
    desc: "Students apply for incubation through a guided form. Coordinators review, update status, leave notes, and mark approved startups as incubatees — all inside the platform.",
  },
  {
    icon: FolderKanban,
    title: "ProjectHub",
    desc: "Manage mini-projects and main projects: team formation, mentor assignment, submission upload, marks entry, and a live exhibition view — one module, full academic lifecycle.",
  },
  {
    icon: Layers,
    title: "Multi-tenant, institute-branded",
    desc: "Each institute gets its own Turso DB, KV configuration and allowed email domain. Modules are toggled per-institute — no shared data, no shared schema.",
  },
  {
    icon: ShieldCheck,
    title: "Role-based access throughout",
    desc: "Student, incharge, organiser, coordinator, superadmin — every action is checked against the caller's role, scoped to their institute and, where applicable, their event or hall.",
  },
];

// ── Module cards for How it Works / Modules page ──────────────────────────
export const MODULES = [
  {
    icon: CalendarCheck,
    label: "Events & Halls",
    color: "from-cobalt-500 to-cobalt-700",
    headline: "Book → Approve → Run → Certify",
    steps: [
      { icon: ClipboardList, text: "Faculty or club requests a hall for a date and slot" },
      { icon: ShieldCheck,   text: "Hall incharge reviews and approves (or declines)" },
      { icon: Megaphone,     text: "Organiser publishes an event page and opens registration" },
      { icon: ScanLine,      text: "QR check-in at the gate; verifiable certificates issued after" },
    ],
  },
  {
    icon: GraduationCap,
    label: "Graduation Day",
    color: "from-indigo-500 to-cobalt-700",
    headline: "Confirm → Seat → Scan → Report",
    steps: [
      { icon: FileCheck2,    text: "Students confirm attendance via email OTP link" },
      { icon: Users,         text: "Guardians register a seat against the student's USN" },
      { icon: ScanLine,      text: "Staff scan QR tickets at the door; kiosk display updates live" },
      { icon: Award,         text: "Dashboard shows live attendance and generates reports" },
    ],
  },
  {
    icon: Rocket,
    label: "BICEP Incubation",
    color: "from-violet-500 to-indigo-600",
    headline: "Apply → Review → Admit → Track",
    steps: [
      { icon: ClipboardList, text: "Student submits a guided 4-step incubation application" },
      { icon: ShieldCheck,   text: "Coordinator reviews details, updates status and leaves notes" },
      { icon: Award,         text: "Approved startup marked as incubatee with cohort tag" },
      { icon: Megaphone,     text: "BICEP events published on the same platform" },
    ],
  },
  {
    icon: FolderKanban,
    label: "ProjectHub",
    color: "from-cyanx to-cobalt-500",
    headline: "Form teams → Submit → Mark → Exhibit",
    steps: [
      { icon: Users,         text: "Teams register; mentors assigned by department" },
      { icon: FileCheck2,    text: "Teams upload deliverables through the submission portal" },
      { icon: ClipboardList, text: "Mentors and staff enter marks inside the platform" },
      { icon: Megaphone,     text: "Exhibition view gives visitors a live project directory" },
    ],
  },
];

// ── Venues on the platform ────────────────────────────────────────────────
export const HALLS = [
  { code: "BSN",  name: "BSN Auditorium" },
  { code: "APJ",  name: "APJ Hall" },
  { code: "A226", name: "A-226 Seminar Hall" },
  { code: "A116", name: "A-116 Seminar Hall" },
  { code: "AMPH", name: "Amphitheatre" },
  { code: "EST",  name: "EST Hall" },
  { code: "LBA",  name: "LBA Hall" },
];

// ── Hero stats ────────────────────────────────────────────────────────────
export const STATS = [
  ["4+",   "campus modules"],
  ["7",    "bookable halls"],
  ["OTP",  "institute SSO"],
];

export { MapPin, Building2 };
